import SearchBarFilters from './SearchBarFilters'
import { useContext, useEffect, useState } from "react";
import { Loader, SettingsIcon, Alert} from "@fluentui/react-northstar";
import { app, call} from '@microsoft/teams-js';
import "./DevicesViewTab.css";
import { TeamsFxContext } from "../Context";
import {getDevices, getDevicesStatus} from '../../apis/cyberGateManagementApi/cyberGateApiClient'
import DeviceCard from './DeviceCard'
import { Device, DeviceStatus } from './../../apis/cyberGateManagementApi/types';
import {LocationsFilters, DevicesFilters, StatusFilters} from './filters.constant'
import * as misc from '../../utils/misc';


export interface UsersData {
  deviceId: string;
  deviceDescription: string;
  userPrincipalName?: string;
  applicationInstanceStatus?: string;
  enabled: boolean;
  recorded: boolean;
  twoWayVideo: boolean;
  incomingCalls: boolean;
  location?: string;
  type?: string;
  isOnline?: boolean
}

export interface UsersSortData{
  [key: string]: UsersData[]
}

export interface Filters {
  deviceType: string 
  location: string
  term: string 
  status: string
}


export default function DevicesViewTab() {

  const { themeString, teamsfx, inTeams, theme  } = useContext(TeamsFxContext);
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [usersSortedData, setUsersSortedData] = useState<UsersSortData>({})
  const [devices, setDevices] = useState<Device[]>([])

  const [filters, setFilters] = useState<Filters | null>(null)
 
  useEffect(() => {

    if (inTeams) {

      const fetchUserProfile = async () => {
        if (!teamsfx){
          return;
        }

        let userInfo = await teamsfx.getUserInfo();
        setCurrentUserEmail(userInfo.preferredUserName);
      }

      fetchUserProfile()
        .catch((error) => setError(error));
    }
  }, [inTeams]);

  useEffect(() => {

    if(currentUserEmail === ''){
      return;
    }

    setLoading(false);
    getUserDevices()

  }, [currentUserEmail]);

  useEffect(() => {
    if(loading === false || error !== undefined){
      app.notifySuccess();
    }
  }, [loading, error]);

  useEffect(() => {
    if(devices.length) {
      getStatuses()
    }
  }, [devices]);


  const getUserDevices = async () => {
    setLoading(true);
    const devices: Device[] = await getDevices()
    setDevices(devices)
    setUsersSortedData(setUsersDevicesCardDataForUI(devices, []))
    setLoading(false);
  }

  const getStatuses = async () => {
    const status: DeviceStatus[] = await getDevicesStatus()
    setUsersSortedData(setUsersDevicesCardDataForUI(devices, status))
  }

  const setupCall = async (userPrincipalName: string) => {
    console.log(userPrincipalName);
    const startCallParams: call.StartCallParams = {
      source: currentUserEmail,
      targets: [userPrincipalName]
    };

    try {
      await call.startCall(startCallParams);
    }
    catch {
      await misc.sleep(2000);
      call.startCall(startCallParams);
    }
  };

  const setUsersDevicesCardDataForUI = (userDevices: Device[], userDevicesStatus: DeviceStatus[]) => {
    const concatUsersData: UsersData[]  = userDevices.map(item => {
      const status = userDevicesStatus.find(el => el.deviceId === item.deviceId)
      return {...item, ...status}
    })
    .sort((a: UsersData, b: UsersData) => a.location && b.location ? a.location.toString().localeCompare(b.location.toString()) : 0);

    const sortedUsersLocation = groupUsersByLocation(concatUsersData)

    const locations: string[] = Object.keys(sortedUsersLocation)
    
    locations.map(location => sortedUsersLocation[location]
      .sort((a: UsersData, b: UsersData) => a.deviceDescription.toString().localeCompare(b.deviceDescription.toString())))

    return sortedUsersLocation
  }

  const groupUsersByLocation = (usersDevicesCard: UsersData[]) => {

    const usersWithoutLocation: UsersSortData = {others: []}
    const groupUsersLocation = usersDevicesCard.reduce((group: UsersSortData, user: UsersData) => {
      const { location } = user;

      if (!location) {
        usersWithoutLocation.others.push(user)
      }else {
        group[location] = group[location] ?? [];
        group[location].push(user);
      }
      return group;
    }, {});

    const groupAllUsers: UsersSortData = {...groupUsersLocation, ...usersWithoutLocation}
    return groupAllUsers
  }

  const filterDevicesByType = (items: UsersData[], deviceType: string | undefined) => {
    switch (deviceType) {
        case DevicesFilters.All:
            return items;
        case DevicesFilters.Intercom:
        case DevicesFilters.Pager:
        case DevicesFilters.Camera:
            return items.filter(el => el.type === deviceType);
        case DevicesFilters.Others:
            return items.filter(el => !el.type);
        default:
            return items;
    }
}

const filterDevicesByStatus = (items: UsersData[], status: string ) => {
    if (status === StatusFilters.All) {
        return items;
    }
    if (status === StatusFilters.Online) {
      return items.filter(el => el.isOnline)
    }
    return items.filter(el => !el.isOnline)
}

  const filterDevices = (usersSortedData: UsersSortData, filters: Filters | null) => {
    
    if(!filters){
      return usersSortedData;
    }

    const { deviceType, location, term, status } = filters
    const currentLocation = location === LocationsFilters.All ? Object.keys(usersSortedData) : [location]
    const findDevicesCards = currentLocation.reduce((acc: UsersSortData, item ) => {

      if (!item){
        return acc;
      }

      const data = !term ? usersSortedData[item] : usersSortedData[item].filter(({ deviceDescription }) => deviceDescription.toLowerCase().includes(term?.toLowerCase() || ''))
      const filterDevices = filterDevicesByType(data, deviceType)
      const filterStatus = filterDevicesByStatus(filterDevices, status)
      acc[item] = filterStatus
      return acc;
    }, {})

    return findDevicesCards
  }

  const renderActiveFilters = (allUsersDevices: UsersSortData) => {

    const allDevicesList = Object.values(DevicesFilters).slice(1)
    const allLocationsList = Object.keys(usersSortedData)
    const activeLocationList = Object.keys(allUsersDevices).filter(location => allUsersDevices[location].length)
    const activeStatus  = Object.values(allUsersDevices).filter(el => el.length).flat(1)
    const devicesType = activeStatus.map(({type}) => !type ? DevicesFilters.Others : type)
    const statusOnline = activeStatus.filter(el => el.isOnline)

    const selectedUsers = activeLocationList.map(el => {
      const type = filterDevicesByType(usersSortedData[el], filters?.deviceType)
      const activeTypes = !filters?.term ? type : type.filter(el => el.deviceDescription.toLowerCase().includes(filters.term.toLowerCase()))
      return activeTypes
    }).flat(1)

    let statuses = !statusOnline.length && selectedUsers.length === activeStatus.length
    ? [StatusFilters.Offline] 
    : (statusOnline.length !== activeStatus.length || selectedUsers.length !== activeStatus.length ? [StatusFilters.Offline, StatusFilters.Online] : [StatusFilters.Online])

    if(!activeStatus.length){
      statuses = []
    }

    const availableDevicesList = filters?.location === LocationsFilters.All ? allDevicesList : activeLocationList.map(el => {
      const availableDevices = usersSortedData[el].map(el => !el.type ? DevicesFilters.Others : el.type)
      return Array.from(new Set(availableDevices))
    })
    
    const filtredLocations = filters?.deviceType === DevicesFilters.All && filters?.status === StatusFilters.All && !filters?.term ? allLocationsList : activeLocationList
    const activeDevices = !filters?.term && filters?.status === StatusFilters.All ? availableDevicesList.flat(1) : Array.from(new Set(devicesType))

    return [filtredLocations, statuses, activeDevices]
}

  const visableDevices = filterDevices(usersSortedData, filters)
  const usersLocationGroup = Object.keys(visableDevices)
  const filtersListActive = renderActiveFilters(visableDevices)
  const isFiltersResultFound = Object.values(visableDevices).filter(el => el.length)
  
  return (
    <div className="devices-tab-page">
      <div className="devices-tab-container">
        <SearchBarFilters onChangeFilters={setFilters} filtersListActive={filtersListActive} theme={theme} themeString={themeString}/>
        {!isFiltersResultFound.length && 
        <div style={{width: '100%', marginTop: 20}}>
          <Alert
            warning
            icon={<SettingsIcon />}
            header="No result found."
            content="Please change your filters setting"
            variables={{warningBorderColor: themeString === 'default' ? 'white' : 'black', warningColor: themeString === 'default' ? 'black' : 'white', warningBackgroundColor: themeString === 'default' ? 'white' : (themeString === 'dark' ? 'rgb(32, 31, 31)' : 'black')}}
          />
          </div>}
        {!loading && <Loader styles={{ margin: '325px auto' }} /> && usersLocationGroup.map((location: string) => (
          <div className='location-cards-block' key={location}>
            <span className='location' >{location === 'others' && visableDevices[location].length > 0 ? 'Other locations' : (visableDevices[location].length > 0 && location)}</span>
            <div className='devices-cards'>
              {visableDevices[location].map((users: UsersData) => (<DeviceCard setupCall={setupCall} key={users.deviceId} users={users} themeString={themeString}/>))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}